import { Component, Element, Prop, h, Listen, State } from '@stencil/core';
import { AiIconType } from '../../utils/icon-types';

@Component({
  tag: 'biel-chat',
  styleUrl: 'biel-chat.css',
  shadow: true,
})
export class BielChat {
  @State() isUserScrollingUp: boolean = false;
  @State() isStreaming: boolean = false;

  private scrollbarHideTimer: ReturnType<typeof setTimeout> | null = null;
  private scrollRafId: number | null = null;
  private isInitialLoad: boolean = true;

  @Prop() messages: {
    text: string;
    sender: 'user' | 'ai';
    messageId?: string;
    feedback?: 0 | 1;
    sources?: { title: string; url: string }[];
    isPartial: boolean;
    partialId?: number;
    incomplete?: boolean;
  }[] = [];
  @Prop() isFullscreen: boolean;
  @Prop() isLoading: boolean = false;
  @Prop() suggestedQuestions: string[];
  @Prop() welcomeMessage: string;
  @Prop() suggestedQuestionsTitle: string;
  @Prop() loadedChat: boolean;
  @Prop() hideFeedback: boolean;
  @Prop() modalPosition: string;
  @Prop() embeddedMode: boolean = false;
  @Prop() bielId: string;
  @Prop() sourcesText: string = 'Sources';
  @Prop() aiIcon: AiIconType = 'bot';
  @Prop() hideAvatars: boolean = false;
  @Prop() hideSources: boolean = false;
  @Prop() showContinueButton: boolean = false;
  @Prop() assistantLabel: string = 'Assistant';

  @Element() el: HTMLElement;

  private chatContainer: HTMLElement;

  @Listen('addMessageText', { target: 'body' })
  handleTextUpdate(event: CustomEvent<{ text: string; id: number; bielId: string }>) {
    if (this.bielId === event.detail.bielId) {
      this.isStreaming = true;
      if (!this.isUserScrollingUp) {
        this.scheduleScrollToBottom();
      }
    }
  }

  @Listen('partialMessageComplete', { target: 'body' })
  handlePartialMessageComplete(event: CustomEvent<{ partialId: number; bielId: string }>) {
    if (this.bielId === event.detail.bielId) {
      if (!this.isUserScrollingUp) {
        this.scheduleScrollToBottom();
        // Force scroll after sources/feedback have rendered and painted
        setTimeout(() => {
          if (!this.isUserScrollingUp) {
            this.scrollToBottom();
          }
        }, 300);
      }
      setTimeout(() => {
        this.isStreaming = false;
      }, 200);
    }
  }

  @Listen('scrollToBottom', { target: 'body' })
  handleScrollToBottom(event: CustomEvent<{ bielId: string }>) {
    // Ignore events fired by biel-message during initial historical message render
    if (this.bielId === event.detail.bielId && !this.isInitialLoad) {
      if (!this.isUserScrollingUp) {
        this.scheduleScrollToBottom();
      }
    }
  }

  scheduleScrollToBottom() {
    if (this.scrollRafId !== null) {
      cancelAnimationFrame(this.scrollRafId);
    }
    this.scrollRafId = requestAnimationFrame(() => {
      this.scrollRafId = null;
      this.scrollToBottom();
    });
  }

  scrollToBottom() {
    const container =
      this.chatContainer ?? this.el.shadowRoot.querySelector('.biel-chat-container');
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }

  private onScroll = () => {
    if (!this.chatContainer) return;
    const { scrollTop, scrollHeight, clientHeight } = this.chatContainer;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 100;
    this.isUserScrollingUp = !isAtBottom;

    // Show scrollbar on manual scroll (not during streaming), hide after 1s of inactivity
    if (!this.isStreaming || this.isUserScrollingUp) {
      this.chatContainer.classList.add('is-scrolling');
      if (this.scrollbarHideTimer) clearTimeout(this.scrollbarHideTimer);
      this.scrollbarHideTimer = setTimeout(() => {
        this.scrollbarHideTimer = null;
        this.chatContainer?.classList.remove('is-scrolling');
      }, 1000);
    }
  };

  componentDidLoad() {
    this.chatContainer = this.el.shadowRoot.querySelector('.biel-chat-container');
    if (this.chatContainer) {
      this.chatContainer.addEventListener('scroll', this.onScroll);
    }

    // Always scroll to bottom on mount, after the widget open animation (300ms).
    setTimeout(() => {
      this.isInitialLoad = false;
      this.scrollToBottom();
    }, 300);
  }

  disconnectedCallback() {
    if (this.chatContainer) {
      this.chatContainer.removeEventListener('scroll', this.onScroll);
    }
    if (this.scrollbarHideTimer) clearTimeout(this.scrollbarHideTimer);
    if (this.scrollRafId !== null) cancelAnimationFrame(this.scrollRafId);
  }

  render() {
    return (
      <div
        class={`biel-chat-container ${this.embeddedMode ? 'biel-chat-container--embedded' : ''} ${this.isFullscreen ? 'fullscreen' : ''} ${this.modalPosition} ${this.isStreaming ? 'is-streaming' : ''}`}
        part="container"
      >
        {this.welcomeMessage && (
          <biel-message
            text={this.welcomeMessage}
            sender="ai"
            sourcesText={this.sourcesText}
            aiIcon={this.aiIcon}
            hideAvatars={this.hideAvatars}
            hideSources={this.hideSources}
            assistantLabel={this.assistantLabel}
            exportparts="container: biel-message-container, label: biel-message-label, bubble: biel-message-bubble, content: biel-message-content"
          ></biel-message>
        )}
        {this.suggestedQuestions && this.suggestedQuestions.length > 0 && (
          <biel-suggested-questions
            heading={this.suggestedQuestionsTitle}
            questions={this.suggestedQuestions}
            bielId={this.bielId}
            exportparts="container: biel-suggested-questions-container, title: biel-suggested-questions-title, list: biel-suggested-questions-list, question: biel-suggested-questions-question"
          ></biel-suggested-questions>
        )}
        {this.messages.map((message) => (
          <biel-message
            messageId={message.messageId}
            text={message.text}
            sender={message.sender}
            sources={message.sources}
            feedback={message.feedback}
            hideFeedback={this.hideFeedback}
            bielId={this.bielId}
            isPartial={message.isPartial}
            partialId={message.partialId}
            sourcesText={this.sourcesText}
            aiIcon={this.aiIcon}
            hideAvatars={this.hideAvatars}
            hideSources={this.hideSources}
            assistantLabel={this.assistantLabel}
            exportparts="container: biel-message-container, label: biel-message-label, bubble: biel-message-bubble, content: biel-message-content"
          ></biel-message>
        ))}
        {this.showContinueButton && (
          <div class="biel-continue-button-container" part="continue-container">
            <button
              part="continue-button"
              class="biel-continue-button"
              onClick={() => {
                const event = new CustomEvent('continueClicked', {
                  detail: { bielId: this.bielId },
                  bubbles: true,
                });
                document.body.dispatchEvent(event);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="lucide lucide-fast-forward-icon lucide-fast-forward"
              >
                <path d="M12 6a2 2 0 0 1 3.414-1.414l6 6a2 2 0 0 1 0 2.828l-6 6A2 2 0 0 1 12 18z" />
                <path d="M2 6a2 2 0 0 1 3.414-1.414l6 6a2 2 0 0 1 0 2.828l-6 6A2 2 0 0 1 2 18z" />
              </svg>
              Continue generating
            </button>
          </div>
        )}
        {this.isLoading && (
          <div class="loading-animation" part="loading">
            <div class="dot"></div>
            <div class="dot"></div>
            <div class="dot"></div>
          </div>
        )}
      </div>
    );
  }
}
